import type {
	DocsDocumentationGuideDocument,
	DocsPagesDocument,
	DocsProjectDocument,
	VersionBaseSchema,
} from '@hashnode/database-schema'

import { createCommunityOrigin } from './community-urls'
import { communityDomainNames, docsDomainNames, protocol } from './constants'
import type { Environment } from './types'

/**
 * Creates the origin address for a project.
 */
export const createDocsProjectOrigin = ({
	project,
	environment,
}: {
	project: Pick<DocsProjectDocument, 'subdomain' | 'customDomain'>
	environment: Environment
}) => {
	const { subdomain: _subDomain, customDomain } = project

	const hasReadyDomain = !!customDomain?.domain && customDomain.status === 'VERIFIED'

	// always use prod as default to make sure prod works
	let subDomain = hasReadyDomain ? '' : `${_subDomain}.`
	const isNonProd = environment !== 'production'
	if (isNonProd) {
		subDomain = `${_subDomain}.`
	}

	let domainName = hasReadyDomain ? customDomain.domain : docsDomainNames.production
	if (isNonProd) {
		domainName = docsDomainNames[environment]
	}
	return `${protocol[environment]}://${subDomain}${domainName}`
}

/**
 * Creates the URL for a guide in a project.
 */
export const createDocsGuideUrl = ({
	project,
	guideSlug,
	environment,
}: {
	project: Pick<DocsProjectDocument, 'subdomain' | 'customDomain'>
	guideSlug: string
	environment: Environment
}) => {
	const origin = createDocsProjectOrigin({
		project,
		environment,
	})
	return `${origin}/${guideSlug}`
}

/**
 * Creates the URL for a page in a guide.
 *
 * Returns `null` if the page is not published or public.
 */
export const createDocsPageUrl = (args: {
	project: Pick<DocsProjectDocument, 'subdomain' | 'customDomain'>
	guide: Pick<DocsDocumentationGuideDocument, 'slug'>
	version: Pick<VersionBaseSchema, 'isDefault' | 'slug'>
	page: Pick<DocsPagesDocument, 'path' | 'status' | 'visibility'>
	environment: Environment
}) => {
	const { project, guide, version, page, environment } = args

	if (page.status !== 'PUBLISHED' || page.visibility !== 'PUBLIC') {
		return null
	}

	const versionPath = version.isDefault ? '' : `/${version.slug}`
	const guideUrl = createDocsGuideUrl({ project, guideSlug: guide.slug, environment })
	return `${guideUrl}${versionPath}${page.path}`
}

export const createDocsMemberInviteUrl = ({
	inviteToken,
	environment,
}: {
	inviteToken: string
	environment: Environment
}) => {
	return `${createCommunityOrigin(environment)}/projects/invite/${inviteToken}`
}

/**
 * Creates page path for a give operations in the API reference guide.
 * This is used to create unique paths for each page in the API reference guide.
 * Cloned from docs-starter repo (decodeOperation).
 */
export const createDocsAPIRefGuidePagePath = ({
	operationId,
	method,
	path,
}: {
	operationId: string | undefined
	method: string
	path: string
}) => {
	const uniquePath = operationId || `${method}_${encodeURIComponent(path)}`
	return uniquePath
}

export const createDocsDashboardUrl = ({
	environment,
	projectId,
}: {
	environment: Environment
	projectId: string
}) => `${protocol[environment]}://${communityDomainNames[environment]}/docs/${projectId}/dashboard`

/**
 * Creates the origin address for a project without considering custom domains.
 * Always uses the subdomain format regardless of custom domain status.
 *
 * @param {Object} params - The parameters object
 * @param {string} params.subdomain - The subdomain of the project
 * @param {Environment} params.environment - The environment (production, development, etc.)
 * @returns {string} The complete origin URL with protocol, subdomain, and domain name
 * @example
 * // Development environment
 * createDocsProjectOriginWithoutSubdomain({
 *   subdomain: 'myproject',
 *   environment: 'development'
 * }) // Returns 'http://myproject.docs.app.localhost'
 *
 * // Production environment
 * createDocsProjectOriginWithoutSubdomain({
 *   subdomain: 'myproject',
 *   environment: 'production'
 * }) // Returns 'https://myproject.hashnode.space'
 */
export function createDocsProjectOriginWithoutSubdomain({
	subdomain,
	environment,
}: {
	subdomain: string
	environment: Environment
}) {
	return `${protocol[environment]}://${subdomain}.${docsDomainNames[environment]}`
}
